import React, { useState } from 'react';
import { Plus, Search, AlertTriangle, Upload } from 'lucide-react';
import { useCompany, useCustomers, useWorkOrders, generateMathProblem } from '../../lib/hooks';
import type { Database } from '../../lib/database.types';
import { WorkOrderCard } from './WorkOrderCard';
import { AddWorkOrderModal } from './AddWorkOrderModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { ImportWorkOrderModal } from '../../components/ImportWorkOrderModal';
import { WorkOrderDetails } from './WorkOrderDetails';

type WorkOrder = Database['public']['Tables']['work_orders']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

const WorkOrders = () => {
  const { company } = useCompany();
  const { customers, addCustomer } = useCustomers();
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const { workOrders, addWorkOrder, deleteWorkOrder, deleting, loading, error } = useWorkOrders();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMathModal, setShowMathModal] = useState<{ orderId: string; problem: any } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredWorkOrders = workOrders.filter(order => 
    (statusFilter === 'all' || order.status === statusFilter) &&
    (order.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.service_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.status.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const statusOptions = [
    'Received',
    'Customer Notified',
    'Awaiting Customer Response',
    'Waiting for Parts',
    'Technician Ordering Parts',
    'Unable to Reach Customer',
    'Canceled',
    'Reassigned',
    'Visit Scheduled',
    'Visit Completed',
    'Needs Second Opinion',
    'Under Research',
    'Billed',
    'Paid'
  ];

  if (selectedOrder) {
    return (
      <WorkOrderDetails
        order={selectedOrder}
        customer={customers.find(c => c.id === selectedOrder.customer_id)}
        onClose={() => setSelectedOrder(null)}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
          <p className="text-gray-600">Manage and track service work orders</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowImportModal(true)}
            disabled={!!error}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Upload className="w-5 h-5 mr-2" />
            Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={!!error}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Work Order
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search work orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={!!error}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
        <div className="relative max-w-md">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="all">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredWorkOrders.map((order) => (
          <WorkOrderCard 
            key={order.id} 
            order={order} 
            customer={customers.find(c => c.id === order.customer_id)}
            onDeleteClick={async (id) => {
              try {
                const problem = await generateMathProblem();
                setShowMathModal({ orderId: id, problem });
              } catch (err) {
                console.error('Failed to generate math problem:', err);
                alert('Unable to verify deletion at this time. Please try again.');
              }
            }}
            onDelete={deleteWorkOrder}
            isDeleting={deleting[order.id] || false}
            onView={() => setSelectedOrder(order)}
          />
        ))}
      </div>

      {showMathModal && (
        <DeleteConfirmationModal
          problem={showMathModal.problem}
          onClose={() => setShowMathModal(null)}
          onConfirm={async (answer) => {
            if (answer === showMathModal.problem.answer) {
              await deleteWorkOrder(showMathModal.orderId);
              setShowMathModal(null);
            } else {
              alert('Incorrect answer. Please try again.');
            }
          }}
        />
      )}

      {showAddModal && (
        <AddWorkOrderModal
          onClose={() => setShowAddModal(false)}
          onAdd={addWorkOrder}
          customers={customers}
          services={company?.services || []}
          brands={company?.brands || []}
        />
      )}
      
      {showImportModal && (
        <ImportWorkOrderModal
          onClose={() => setShowImportModal(false)}
          onImport={async (data) => {
            try {
              // First create or find the customer
              const customerData = {
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email || '',
                phone: data.phone,
                address: data.address,
                city: data.city,
                state: data.state,
                zip_code: data.zipCode
              };
              
              const { customer } = await addCustomer(customerData);
              
              // Then create the work order
              await addWorkOrder({
                customer_id: customer.id,
                service_type: data.serviceType || 'Appliance',
                status: 'Received',
                priority: 'medium',
                brand: data.brand,
                svo: data.svo,
                model_number: data.modelNumber,
                serial_number: data.serialNumber,
                description: data.description || 'Imported work order',
                notes: data.notes
              });
              
              setShowImportModal(false);
            } catch (err) {
              console.error('Error importing work order:', err);
              alert('Failed to import work order. Please try again.');
            }
          }}
        />
      )}
    </div>
  );
};

export default WorkOrders;